import { ApiError } from "@nabble/sdk";

/** Base path of the module's API (contract: docs/modules/drive.md). */
export const BASE = "/api/drive";

const TOO_MANY_REQUESTS = 429;
const SERVER_ERROR = 500;

const MESSAGES: Record<string, string> = {
  office_native_only: "This file was created or changed outside Nabble. Use its preview or download the original. Native editing currently supports files created in Nabble.",
  office_invalid_document: "This document exceeds the editor limits or contains unsupported data. Your changes have not been saved.",
  office_invalid_file: "This file could not be read safely. Download the original from Drive.",
  office_unsupported: "Office editing supports .docx and .xlsx files.",
  office_in_use: "This document is still open in Office. Ask everyone to close it, wait a few seconds, and try again.",
  office_conflict: "Someone saved a newer version. Your changes are still here. Download your draft before reloading the saved version.",
  office_session_expired: "This editing session ended. Reopen the document to continue.",
  office_save_failed: "The editor could not save to Drive. Keep the document open and try saving again.",
  not_found: "We couldn't find this item. Someone may have deleted or moved it.",
  forbidden: "You don't have permission to do this.",
  validation: "Please check the information you entered.",
  incomplete: "The upload didn't finish. Please try again.",
};

/** Messages for one field's code, when the generic one would be unclear. */
const FIELD_SPECIFIC: Record<string, string> = {
  "parentId.invalid": "It can't go there: not into itself, a folder inside it, or another organization's drive.",
  "name.invalid": "Names can't contain / or \\, invisible characters, or be just a dot.",
};

const FIELD_MESSAGES: Record<string, string> = {
  required: "Give it a name.",
  too_long: "The name is too long.",
  invalid: "This isn't allowed here.",
  taken: "There's already an item with this name here.",
  too_big: "The file is bigger than 100 MB.",
};

export function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

/** The code of one field of a `validation` error (`taken`, `too_long`…), or null. */
export function fieldError(err: unknown, field?: string): string | null {
  if (!(err instanceof ApiError) || err.code !== "validation") return null;
  const fields = (err.body as { fields?: Record<string, string> }).fields ?? {};
  if (field) return fields[field] ?? null;
  return Object.values(fields)[0] ?? null;
}

/** True when the server refused a name because a sibling already has it. */
export function isNameTaken(err: unknown): boolean {
  return fieldError(err) === "taken";
}

export function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === TOO_MANY_REQUESTS) return "Too many actions in a row. Please wait a moment.";
    const field = fieldError(err);
    const [name] = Object.keys((err.body as { fields?: Record<string, string> }).fields ?? {});
    const specific = name && field ? FIELD_SPECIFIC[`${name}.${field}`] : undefined;
    if (specific) return specific;
    if (field && FIELD_MESSAGES[field]) return FIELD_MESSAGES[field];
    return MESSAGES[err.code] ?? (err.status >= SERVER_ERROR ? "The service isn't available right now. Please try again in a few minutes." : fallback);
  }
  if (err instanceof TypeError) return "We couldn't reach the server. Please check your connection.";
  return fallback;
}
